// SF4 — Top Performing SKUs.
//
// The products generating the most revenue in the window, ranked by revenue
// only. Units are a column and never a ranking key: a cheap high-volume SKU is
// not a top performer by revenue, and ordering by units would say it is.
//
// Growth is the column that matters most. A top-ranked product declining
// sharply is the most urgent row on the page.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../SalesPerformance.module.css';
import SkuRankTable from './SkuRankTable';
import { ClippedWindow, count } from './format';
import { getTopSkus } from '../services/salesPerformanceApi';

const TopSkus = ({ window: reportingWindow, onLoadingChange }) => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const { start, end } = reportingWindow || {};

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token || !start || !end) return;
    setLoading(true);
    onLoadingChange?.(true);
    setError(null);
    try {
      setData(await getTopSkus(token, { start, end }));
    } catch (err) {
      setError(err);
      setData(null);
    } finally {
      setLoading(false);
      onLoadingChange?.(false);
    }
  }, [token, start, end, onLoadingChange]);

  useEffect(() => { load(); }, [load]);

  // Unmounting mid-load would otherwise leave the page's Apply button
  // stuck reporting this component as still running.
  useEffect(() => () => onLoadingChange?.(false), [onLoadingChange]);

  const rows = data?.rows || [];

  return (
    <section className={local.card}>
      <div className={local.cardHead}>
        <h2 className={local.cardTitle}>Top performing SKUs</h2>
        {data && (
          <span className={local.pill}>
            {rows.length} of {count(data.products_with_sales)} with sales
          </span>
        )}
      </div>
      <p className={local.muted}>
        Ranked by revenue. Units are shown for context and do not affect the
        order. Variation families rank as one product, under their parent ASIN.
      </p>
      <ClippedWindow data={data} />

      {error && (
        <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
          <FiAlertTriangle className={local.bannerIcon} />
          <div>
            <strong>
              {error.notConnected ? 'No Amazon account connected'
                : error.reportUnavailable ? 'Data not ready' : 'Could not load the ranking'}
            </strong>
            <div>{error.message}</div>
          </div>
        </div>
      )}

      {!error && (
        <SkuRankTable
          rows={rows}
          loading={loading}
          emptyMessage="No products sold in this period."
        />
      )}

      {data?.notes?.length > 0 && (
        <ul className={local.noteList}>
          {data.notes.map((note) => (
            <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default TopSkus;
